import Icon from '../../../Icon/Icon';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../../../utils/functions';
import ScreensDetails from '../ScreensDetails/ScreensDetails';

const PredictionDetails = ({
    setHandleDataSchedule,
    handleDataSchedule,
    campaign,
    campaignPrediction,
    isDelayed,
    campaignScreensStatus,
}) => {
    const { t } = useTranslation();
    const [showScreensNotBuying, setShowScreensNotBuying] = useState(false);

    const screenTranslations =
        'sections.campaigns.new-campaign.wizard-screens.screens.';

    const allScreens = useMemo(() => {
        try {
            return campaignPrediction?.screensDetails
                ? [
                      ...(campaignPrediction.screensDetails.offline?.screen ||
                          []),
                      ...(campaignPrediction.screensDetails.online?.screen ||
                          []),
                  ]
                : [];
        } catch {
            return [];
        }
    }, [campaignPrediction]);

    const publishersWithStatus = useMemo(() => {
        const map = {};
        const { approvedScreens, disapprovedScreens, pendingScreens } =
            campaignScreensStatus?.status || {};
        approvedScreens?.publishers?.forEach(
            (p) => (map[p] = t(screenTranslations + 'approved'))
        );
        disapprovedScreens?.publishers?.forEach(
            (p) => (map[p] = t(screenTranslations + 'rejected'))
        );
        pendingScreens?.publishers?.forEach(
            (p) => (map[p] = t(screenTranslations + 'pending'))
        );
        return map;
    }, [campaignScreensStatus, t]);

    const hours = useMemo(() => {
        const h = { AM: [], PM: [] };
        //todo: check hours_enabled format for launched campaigns, it was crashing without array validation
        if (
            campaign?.hoursEnabledSchedule?.length &&
            Array.isArray(campaign.hoursEnabledSchedule)
        ) {
            campaign.hoursEnabledSchedule.forEach((hour) => {
                const [period, formatted] = moment(hour, 'HH')
                    .format('A-h')
                    .split('-');
                h[period]?.push(formatted);
            });
        }
        h.AM.sort((a, b) => a - b);
        h.PM.sort((a, b) => a - b);
        return h;
    }, [campaign]);

    const concatDailyCap = campaignPrediction
        ? `${campaign?.currency_name} ${
              campaign?.currency?.symbol
          } ${formatNumber(campaignPrediction?.dailySpent, 'currency')} / ${
              campaignPrediction?.dailyImpressions
          } ${t(
              'sections.campaigns.new-campaign.wizard-review.blocks.basics.impressions'
          )}`
        : '';

    const currentHour = moment(moment().hour(), 'H').format('hA');

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget)
            setHandleDataSchedule(!handleDataSchedule);
    };

    return (
        <>
            <div
                onClick={handleOverlayClick}
                className={`fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-500 z-40 ${
                    handleDataSchedule
                        ? 'opacity-0 pointer-events-none'
                        : 'opacity-100'
                }`}></div>

            <div
                className={`fixed right-0 top-0 h-screen w-[420px] bg-white border-l border-gray-200 transform transition-transform duration-500 ease-in-out z-50 ${
                    handleDataSchedule ? 'translate-x-full' : 'translate-x-0'
                }`}>
                <button
                    onClick={() => setHandleDataSchedule(!handleDataSchedule)}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
                    aria-label="Close details panel">
                    <Icon name="times" size="lg" />
                </button>

                <div className="p-6 pt-14 overflow-y-auto h-full space-y-6">
                    {/* Campaign info */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800">
                            {t(
                                'sections.campaigns.new-campaign.wizard-screens.screens.diagnostics-title'
                            )}
                            <span className="text-primary ml-1">
                                #{campaign?.id}
                            </span>
                        </h3>
                        <p className="text-sm text-gray-500">
                            {campaign?.name}
                        </p>
                    </div>

                    <section>
                        <h4 className="font-semibold text-gray-700 mb-2 capitalize">
                            {t(screenTranslations + 'publishers')}:
                        </h4>
                        <ul className="space-y-1 text-sm">
                            {Object.entries(publishersWithStatus).map(
                                ([publisher, status]) => (
                                    <li
                                        key={publisher}
                                        className="flex justify-between bg-gray-50 rounded-md px-2 py-1">
                                        <span className="font-medium text-gray-800">
                                            {publisher}
                                        </span>
                                        <span
                                            className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                                status.includes('approved')
                                                    ? 'bg-green-100 text-green-700'
                                                    : status.includes(
                                                          'rejected'
                                                      )
                                                    ? 'bg-red-100 text-red-700'
                                                    : 'bg-yellow-100 text-yellow-700'
                                            }`}>
                                            {status}
                                        </span>
                                    </li>
                                )
                            )}
                        </ul>
                    </section>

                    <section>
                        <h4 className="font-semibold text-gray-700 mb-2 capitalize">
                            {t(
                                'sections.campaign-list.card-campaign.screen-status'
                            )}
                        </h4>
                        <div className="space-y-1 text-sm">
                            <p>
                                <strong>
                                    {
                                        campaignScreensStatus?.status
                                            ?.approvedScreens?.total
                                    }
                                </strong>{' '}
                                {t(screenTranslations + 'screens-approved')}.
                            </p>
                            <p>
                                <strong>
                                    {
                                        campaignScreensStatus?.status
                                            ?.disapprovedScreens?.total
                                    }
                                </strong>{' '}
                                {t(screenTranslations + 'screens-rejected')}.
                            </p>
                            <p>
                                <strong>
                                    {
                                        campaignScreensStatus?.status
                                            ?.pendingScreens?.total
                                    }
                                </strong>{' '}
                                {t(screenTranslations + 'screens-pending')}.
                            </p>
                        </div>
                    </section>

                    {campaignPrediction && (
                        <section>
                            <p className="text-sm text-gray-700">
                                <strong>
                                    {campaignPrediction?.notBuyingScreen}
                                </strong>{' '}
                                {t(screenTranslations + 'no-buying')}.
                            </p>

                            {allScreens.length > 0 && (
                                <>
                                    <button
                                        className="w-full flex items-center justify-between text-sm font-semibold text-gray-700 hover:text-primary mt-3"
                                        onClick={() =>
                                            setShowScreensNotBuying(
                                                !showScreensNotBuying
                                            )
                                        }>
                                        <span>
                                            {t(
                                                screenTranslations +
                                                    'here-details'
                                            )}
                                        </span>
                                        <Icon
                                            name={
                                                showScreensNotBuying
                                                    ? 'angle-up'
                                                    : 'angle-down'
                                            }
                                        />
                                    </button>
                                    <div
                                        className={`transition-all overflow-hidden ${
                                            showScreensNotBuying
                                                ? 'max-h-[65vh] mt-2'
                                                : 'max-h-0'
                                        }`}>
                                        <div className="space-y-3 pr-2">
                                            {[...allScreens]
                                                .sort(
                                                    (a, b) =>
                                                        Number(b.isBuying) -
                                                        Number(a.isBuying)
                                                )
                                                .map((screen, index) => (
                                                    <ScreensDetails
                                                        key={index}
                                                        name={screen.name}
                                                        isOnline={
                                                            screen.isOnline
                                                        }
                                                        currencyMatched={
                                                            screen.currencyMatched
                                                        }
                                                        currency_name={
                                                            campaign.currency_name
                                                        }
                                                        sizesMatched={
                                                            screen.sizesMatched
                                                        }
                                                        audienceMached={
                                                            screen.audienceMached ||
                                                            false
                                                        }
                                                        size={screen.size}
                                                        isBuying={
                                                            screen.isBuying
                                                        }
                                                        screenTranslations={
                                                            screenTranslations
                                                        }
                                                        priceMatched={
                                                            screen.priceMatched
                                                        }
                                                        screen={screen}
                                                        screenId={screen.id}
                                                    />
                                                ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </section>
                    )}

                    {campaignPrediction && (
                        <section>
                            <h4 className="font-semibold text-gray-700 mb-1 capitalize">
                                {t(
                                    'sections.campaigns.new-campaign.wizard-review.blocks.basics.daily-cap'
                                )}
                            </h4>
                            <p className="text-sm text-gray-700">
                                {concatDailyCap}
                            </p>

                            <h4 className="font-semibold text-gray-700 mt-2 capitalize">
                                {t(screenTranslations + 'current-spent')}:
                            </h4>
                            <p className="text-sm text-gray-700">
                                {formatNumber(
                                    campaignPrediction.totalSpent,
                                    'currency'
                                )}
                            </p>
                        </section>
                    )}

                    {campaignPrediction && (
                        <section className="border-t border-gray-200 pt-4">
                            <div className="flex items-center space-x-2 text-sm">
                                <Icon
                                    name={
                                        isDelayed
                                            ? 'exclamation-triangle'
                                            : 'check'
                                    }
                                    className={
                                        isDelayed
                                            ? 'text-red-500'
                                            : 'text-green-500'
                                    }
                                />
                                <span
                                    className={`${
                                        isDelayed
                                            ? 'text-red-600 font-semibold'
                                            : 'text-green-600'
                                    }`}>
                                    {isDelayed
                                        ? t(screenTranslations + 'no-according')
                                        : t(
                                              screenTranslations +
                                                  'everything-according'
                                          )}
                                    .
                                </span>
                            </div>
                        </section>
                    )}
                </div>
            </div>
        </>
    );
};

PredictionDetails.propTypes = {
    setHandleDataSchedule: PropTypes.func.isRequired,
    handleDataSchedule: PropTypes.bool.isRequired,
    campaign: PropTypes.object.isRequired,
    isDelayed: PropTypes.bool.isRequired,
};

export default PredictionDetails;
